import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery_experimental } from "convex/react";
import type { FunctionReference } from "convex/server";
import type { ExportRequest } from "./ics";
import { errorMessage } from "./errors";
import { deviceTimeZone } from "./deviceTimeZone";

/**
 * The on-tap half of calendar export (issue #656): the ONE arming /
 * settle / in-flight flow both apps' export hooks consume — the mobile
 * hook's (#374) body and the web port's (#375) near-verbatim copy,
 * converged. The seam is a QUERY and the react client runs queries
 * reactively, so the request rides useQuery_experimental with args
 * "skip" until a tap arms it; the first success or refusal resolves
 * the flow and disarms, so the subscription lives for exactly one
 * round trip. The timezone always threads from the ONE shared zone
 * reader ({@link deviceTimeZone}) — floating reminders concretize the
 * way THIS device reads the wall clock. What stays app-side is the
 * platform half: the two query references and the delivery (the web
 * download / the mobile share sheet) arrive through the seam.
 *
 * This module deliberately rides the package's "./calendarExportFlow"
 * subpath export, never the index: the index graph feeds the server
 * (convex/exports.ts imports the shared ICS builder), and a react
 * import must never enter that graph.
 *
 * The experimental read never throws during render — the refusals (a
 * tier the server still refuses, an empty list, an undated item, lost
 * access) arrive as status "error" and surface through onError, never
 * a crashed render. The free refusal never reaches here — the screens
 * branch through resolveExportTap first — and the server re-refuses
 * anyway (the gate is mirrored, never client-only).
 */

/** Which export the viewer asked for (the shared export vocabulary). */
export type { ExportRequest };

/** The export queries' return contract: the server names the file. */
export type CalendarExportPayload = {
  filename: string;
  ics: string;
};

/**
 * The list flavor's query reference. The args are typed as plain
 * strings — the ids arrive as opaque strings here and satisfy the
 * backend's branded GenericId refs structurally, so both apps pass
 * their generated api.exports references unchanged.
 */
export type ListCalendarQuery = FunctionReference<
  "query",
  "public",
  { listId: string; timeZone: string },
  CalendarExportPayload
>;

/** The item flavor's query reference (same contract, item-keyed). */
export type ItemCalendarQuery = FunctionReference<
  "query",
  "public",
  { itemId: string; timeZone: string },
  CalendarExportPayload
>;

/**
 * A refusal or a delivery failure, ready for the surface's error copy.
 * onShared is the mobile share sheet's completion beat (the menu may
 * close once the OS handoff resolved); the web download has no
 * completion step and simply never passes it.
 */
export type CalendarExportCallbacks = {
  onError: (message: string) => void;
  onShared?: () => void;
};

/**
 * The platform delivery, app-side by design (#656): called at most
 * once per armed round trip with the server's payload and the LIVE
 * callbacks. Failure never throws — the delivery reports through
 * callbacks.onError itself, so the flow's own settle stays total.
 */
export type CalendarExportDelivery = (
  payload: CalendarExportPayload,
  callbacks: CalendarExportCallbacks,
) => void;

/** The app-side half the wrapper binds: the queries and the delivery. */
export type CalendarExportSeam = {
  listCalendar: ListCalendarQuery;
  itemCalendar: ItemCalendarQuery;
  deliver: CalendarExportDelivery;
};

export function useCalendarExportFlow(
  callbacks: CalendarExportCallbacks,
  seam: CalendarExportSeam,
) {
  const [request, setRequest] = useState<ExportRequest | null>(null);

  const listResult = useQuery_experimental({
    query: seam.listCalendar,
    args:
      request?.kind === "list"
        ? {
            listId: request.listId,
            timeZone: deviceTimeZone(),
          }
        : "skip",
  });
  const itemResult = useQuery_experimental({
    query: seam.itemCalendar,
    args:
      request?.kind === "item"
        ? {
            itemId: request.itemId,
            timeZone: deviceTimeZone(),
          }
        : "skip",
  });

  const active =
    request === null ? null : request.kind === "list" ? listResult : itemResult;

  // Latest-wins: a callback captured at arm time would deliver into a
  // stale surface after a re-render.
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => {
    if (request === null || active === null) return;
    if (active.status === "success") {
      const { filename, ics } = active.data;
      setRequest(null);
      seam.deliver({ filename, ics }, callbacksRef.current);
    } else if (active.status === "error") {
      setRequest(null);
      // The refusal copy (paid, empty list, undated item, lost access)
      // is the server's message — surfaced through errorMessage, never
      // swallowed.
      callbacksRef.current.onError(
        errorMessage(active.error, "Could not export the calendar"),
      );
    }
    // seam rides the deps as the wrappers' module-level constant it is —
    // never a fresh object per render, so the settle never re-fires.
  }, [request, active, seam]);

  const requestExport = useCallback((r: ExportRequest) => {
    // One export in flight — a second tap during the round trip is a no-op.
    setRequest((cur) => (cur === null ? r : cur));
  }, []);

  return { requestExport };
}
